/**
 * Throttle leading + trailing : exécute immédiatement, puis au plus une fois
 * par `wait` ms, en conservant les derniers arguments pour l'appel final.
 */
export interface ThrottledFn<A extends any[]> {
  (...args: A): void;
  cancel: () => void;
}

export function throttle<A extends any[]>(
  fn: (...args: A) => void,
  wait: number
): ThrottledFn<A> {
  let last = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: A | null = null;

  const throttled = ((...args: A) => {
    const now = Date.now();
    const remaining = wait - (now - last);
    lastArgs = args;
    if (remaining <= 0) {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      last = now;
      fn(...args);
    } else if (!timer) {
      timer = setTimeout(() => {
        last = Date.now();
        timer = null;
        if (lastArgs) fn(...lastArgs);
      }, remaining);
    }
  }) as ThrottledFn<A>;

  throttled.cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    lastArgs = null;
  };

  return throttled;
}
